import type { EventDetails, RegistrationStatus } from "@/modules/events/types/eventDetailsApi"
import type { FunctionComponent, MouseEventHandler } from "react"

import { isBefore } from "date-fns"
import { useParams } from "next/navigation"
import { useEffect } from "react"

import { Button } from "@jobteaser/spark/components/Button"

import {
  EXTERNAL,
  JOBTEASER,
  NO_SPOTS_LEFT,
  REGISTRATION_STATUS_DECLINED,
  REGISTRATION_STATUS_USER_NOT_AVAILABLE,
  WITHOUT_REGISTRATION,
} from "@/modules/events/constants"
import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { useTrackEvent } from "@/modules/tracking/useTrackEvent"

type SubscribeButtonProps = {
  registrationStatus: RegistrationStatus
  handleClick: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
}

export const SubscribeButton: FunctionComponent<SubscribeButtonProps> = ({ registrationStatus, handleClick }) => {
  const locale = useLocale()
  const { t } = useTranslation(["event_details"])
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)
  const trackEvent = useTrackEvent()

  const { registrationDetails, id } = event as EventDetails
  const hostname = typeof window !== "undefined" ? window.location.host : ""
  const [subdomain] = hostname.split(".")

  const { type, startDate } = registrationDetails
  const currentDate = new Date()
  const isEventWithoutRegistration = type === WITHOUT_REGISTRATION
  const isExternalEvent = type === EXTERNAL
  const isJobteaserRegistration = type === JOBTEASER

  const endDate = new Date(event?.endDate || "")
  const registrationOpeningDate = new Date(startDate || "")
  const registrationLimitDate = isEventWithoutRegistration
    ? new Date()
    : new Date(registrationDetails.registrationDeadline)

  const isEventFinished = isBefore(endDate, currentDate)
  const isRegistrationNotOpenYet = isBefore(currentDate, registrationOpeningDate)
  const isRegistrationDeadlineExceeded = isBefore(registrationLimitDate, currentDate)

  const isEventFull = registrationStatus.remainingSpots === NO_SPOTS_LEFT
  const isProfileNotAccepted = registrationStatus.status === REGISTRATION_STATUS_DECLINED
  const isUserNotAvailable = registrationStatus.status === REGISTRATION_STATUS_USER_NOT_AVAILABLE

  const isButtonDisabled =
    isRegistrationNotOpenYet ||
    isRegistrationDeadlineExceeded ||
    isEventWithoutRegistration ||
    isEventFull ||
    isProfileNotAccepted ||
    isUserNotAvailable

  useEffect(() => {
    if (!isButtonDisabled) {
      trackEvent("event_ad_subscribe_display", {
        event_id: id,
        is_visible: true,
        service: "event_details",
        subdomain,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isButtonDisabled])

  if (isEventFinished) {
    return null
  }

  const onClick: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement> = evt => {
    trackEvent("event_ad_subscription_click", {
      context: "event_ad",
      event_ad_id: id,
      service: "event_details",
      trackee_locale: locale,
    })

    if (isJobteaserRegistration) {
      handleClick(evt)
    }
  }

  if (isExternalEvent && !isRegistrationDeadlineExceeded && !isRegistrationNotOpenYet) {
    const { external } = registrationDetails

    return (
      <Button
        data-testid="event-details-registration-details-subscribe-button"
        href={external?.subscriptionUrl}
        onClick={onClick}
        rel="noopener noreferrer"
        renderComponent="a"
        target="_blank"
        variant="highlight"
      >
        {t("event_details.subscribe")}
      </Button>
    )
  }

  return (
    <Button
      data-testid="event-details-registration-details-subscribe-button"
      disabled={isButtonDisabled}
      onClick={onClick}
      variant="highlight"
    >
      {isEventFull ? t("event_details.registration.full") : t("event_details.subscribe")}
    </Button>
  )
}
